import { FunctionComponent, useCallback, useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import styled, { css, useTheme } from 'styled-components'

import { triggerAureusImpression } from '@utils/aureus'
import { Image, Link, TargetContentType, TeaserVideo } from '@utils/cook/types'
import { LIVE_EVENT_DATA_SPORT_TYPES } from '@utils/formatters/common'
import { LiveEventDataSportTypes } from '@utils/formatters/liveEvent'
import useAnimatedPreview from '@hooks/useAnimatedPreview'
import useRefDelegate from '@hooks/useRefDelegate'
import useSportsEvent from '@hooks/useSportsEvent'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import { stripHtml } from '@hooks/useTracking/utils'
import useViewportTracking from '@hooks/useViewportTracking'
import { CookWidget, JSONTypeForCookWidget } from '@widgets/types'
import HockeySoccerSportsFooter from '@components/SportsFooter/HockeySoccer'
import SportLabelContent from '@components/SportsFooter/SportLabelContent'
import { transformDataForSportsComponents } from '@components/SportsFooter/utils'
import TeaserAnimatedPreview from '@components/TeaserAnimatedPreview'
import BlickPlusLogo from '@components/TeaserMiddleToi/BlickPlusLogo'
import Catchword from '@components/TeaserMiddleToi/Catchword'
import ImageComponent from '@components/TeaserMiddleToi/Image'
import Label from '@components/TeaserMiddleToi/Label'
import LoudSpeaker from '@components/TeaserMiddleToi/LoudSpeaker'
import TextWrapper from '@components/TeaserMiddleToi/TextWrapper'
import Title from '@components/TeaserMiddleToi/Title'
import TeaserMiddleToiWrapper from '@components/TeaserMiddleToi/Wrapper'
import VideoLabel from '@components/VideoLabel'

export interface TeaserMiddleToiCommonProps {
  accentColor: string
  title: string
  catchword?: string
  isPlus?: boolean
  articleId?: string
  targetContentType: TargetContentType
  track?: boolean
  link: Link
  image: Image<'16_9_mid_toi'>
  section: {
    name: string
    uniqueName: string
  }
  contentOrigin?: string
  video?: TeaserVideo
  aureusOfferId?: string
  hasSpecialLabel?: boolean
}

export interface TeaserMiddleToiNoCommercialProps {
  commercialType?: undefined
  labelText?: string
}

export interface TeaserMiddleToiNoSportProps {
  typeOfSport?: undefined
}

export interface TeaserMiddleToiSportProps {
  typeOfSport: Extract<LiveEventDataSportTypes, 'soccer' | 'hockey'>
  matchId: string
}

export type TeaserMiddleToiMaybeSportProps =
  | TeaserMiddleToiNoSportProps
  | TeaserMiddleToiSportProps

export type TeaserMiddleToiAPIProps = TeaserMiddleToiCommonProps &
  TeaserMiddleToiNoCommercialProps &
  TeaserMiddleToiMaybeSportProps

const StyledVideoLabel = styled(VideoLabel)`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => css`
    position: absolute;
    bottom: ${spacing8};
    left: ${spacing8};
  `}
`

const StyledHockeySoccerSportsFooter = styled(HockeySoccerSportsFooter)`
  ${({
    theme: {
      colors: { staticNeutral900 },
    },
  }) => css`
    background-color: ${staticNeutral900};
  `}
`

const TeaserMiddleToi: FunctionComponent<TeaserMiddleToiAPIProps> = (props) => {
  const {
    accentColor,
    title,
    link,
    image,
    isPlus,
    catchword,
    targetContentType,
    typeOfSport,
    articleId,
    contentOrigin,
    track,
    video,
    aureusOfferId,
    commercialType,
    hasSpecialLabel,
  } = props
  const queryClient = useQueryClient()
  const isValidSport =
    !!typeOfSport &&
    (
      [
        LIVE_EVENT_DATA_SPORT_TYPES.SOCCER,
        LIVE_EVENT_DATA_SPORT_TYPES.HOCKEY,
      ] as LiveEventDataSportTypes[]
    ).includes(typeOfSport)
  const showCatchword = !!isPlus || !!catchword
  const isCommercial = !!commercialType
  const hasLabelText = !isCommercial && !!props.labelText
  const hasLoudspeaker = hasLabelText && !!hasSpecialLabel

  const { data: sportsEventData } = useSportsEvent({
    matchId: isValidSport ? props.matchId : '',
    typeOfSport: isValidSport ? typeOfSport : 'NO_SPORT',
    shouldRefetch: true,
  })

  const sportsData = sportsEventData
    ? transformDataForSportsComponents(sportsEventData)
    : undefined
  const shouldShowSportsComponents =
    sportsData?.typeOfSport === LIVE_EVENT_DATA_SPORT_TYPES.SOCCER ||
    sportsData?.typeOfSport === LIVE_EVENT_DATA_SPORT_TYPES.HOCKEY

  const {
    colors: { fillSport },
  } = useTheme()

  const labelColor = sportsData?.isLive
    ? undefined
    : isValidSport
      ? fillSport
      : accentColor

  const onImpression = useCallback<TrackingFnType>(() => {
    return {
      event: 'teaser_impression',
      eventCategory: video ? 'video_teaser' : 'article_teaser',
      eventLabel: `${articleId}:${stripHtml(title)}`,
      eventAction: 'impression',
      id: articleId,
      is_branded: !!contentOrigin,
      link_url: link.href,
    }
  }, [video, articleId, title, contentOrigin, link.href])

  const onClick = useCallback<TrackingFnType>(() => {
    return {
      event: 'teaser_click',
      eventCategory: video ? 'video_teaser' : 'article_teaser',
      eventLabel: `${articleId}:${stripHtml(title)}`,
      eventAction: 'click',
      id: articleId,
      is_branded: !!contentOrigin,
      link_url: link.href,
    }
  }, [articleId, contentOrigin, title, video, link.href])

  const trackedOnImpression = useTracking(onImpression)
  const trackedOnClick = useTracking(onClick)

  const shouldBeTracked = !!track

  const finalOnImpression = useCallback(() => {
    triggerAureusImpression({
      aureusOfferId,
      queryClient,
    })

    if (shouldBeTracked) {
      trackedOnImpression()
    }
  }, [aureusOfferId, queryClient, trackedOnImpression, shouldBeTracked])

  const clickableProps = {
    aureusOfferId,
    ...(shouldBeTracked
      ? {
          onClick: trackedOnClick,
        }
      : {}),
    ...link,
  }

  const animatedPreviewRef = useAnimatedPreview({
    video,
    articleUrl: clickableProps.href,
  })

  const viewportTrackingRef = useViewportTracking({
    onImpression: finalOnImpression,
    track: true,
  })

  const ref = useRefDelegate(
    useMemo(
      () => [animatedPreviewRef, viewportTrackingRef],
      [animatedPreviewRef, viewportTrackingRef],
    ),
  )

  return (
    <TeaserMiddleToiWrapper {...clickableProps} ref={ref}>
      <ImageComponent {...image}>
        {hasLoudspeaker && hasLabelText && (
          <LoudSpeaker labelColor={labelColor}>{props.labelText}</LoudSpeaker>
        )}
        {!!video && (
          <TeaserAnimatedPreview video={video} articleUrl={link.href} />
        )}
        {!!props.video && (
          <StyledVideoLabel
            duration={props.video.duration}
            targetContentType={targetContentType}
          />
        )}
        <TextWrapper>
          {!hasLoudspeaker && (hasLabelText || isValidSport) && (
            <Label labelColor={labelColor}>
              {shouldShowSportsComponents ? (
                <SportLabelContent sportsData={sportsData} />
              ) : hasLabelText ? (
                props.labelText
              ) : null}
            </Label>
          )}
          {showCatchword && (
            <Catchword>
              {isPlus && <BlickPlusLogo />}
              {catchword}
            </Catchword>
          )}
          <Title>{title}</Title>
        </TextWrapper>
      </ImageComponent>
      {shouldShowSportsComponents && (
        <StyledHockeySoccerSportsFooter
          sportsData={sportsData}
          viewType="full"
        />
      )}
    </TeaserMiddleToiWrapper>
  )
}

const widget = {
  kind: ['teaser-middle-toi'],
  component: TeaserMiddleToi,
} as const satisfies CookWidget

export type WidgetType = typeof widget

export type JSONWidgetType = JSONTypeForCookWidget<WidgetType>

export default widget
